// src/features/mensagens/utils/formatters.js
// ============================================================================
// 🎨 FORMATEADORES CENTRALIZADOS — ÚNICA FUENTE DE VERDAD
// 📦 V1 ENTERPRISE — Zero Duplicación · pt-BR · WhatsApp-Ready
// ============================================================================
//
// REGLAS DE ORO:
// 1. TODO formateo visual del módulo mensagens pasa por este archivo
// 2. NUNCA llamar toLocaleString() directamente en componentes
// 3. SIEMPRE manejar null/undefined/0 gracefulmente
// 4. Formateo en pt-BR consistente con el sistema
// 5. Funciones PURAS — mismo input = mismo output
// ============================================================================

// ============================================================================
// ⏰ FORMATEO DE TIEMPO PARA CONVERSACIONES (Estilo WhatsApp)
// ============================================================================

/**
 * Formata timestamp para lista de conversas (estilo WhatsApp)
 * - Hoje: "14:30"
 * - Ontem: "Ontem"
 * - Esta semana: "Terça"
 * - Este ano: "15/03"
 * - Anos anteriores: "15/03/2025"
 * 
 * @param {number|Date|null} timestamp - Timestamp em ms ou objeto Date
 * @returns {string} Data/hora formatada
 */
export function formatConversationTime(timestamp) {
  if (!timestamp) return '';
  
  const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
  if (isNaN(date.getTime())) return '';

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const oneWeekAgo = new Date(today);
  oneWeekAgo.setDate(oneWeekAgo.getDate() - 6);

  // Hoje: hora
  if (date >= today) {
    return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  }

  // Ontem
  if (date >= yesterday) {
    return 'Ontem';
  }

  // Esta semana: dia da semana
  if (date >= oneWeekAgo) {
    return date.toLocaleDateString('pt-BR', { weekday: 'long' });
  }

  // Este ano: dia/mês
  if (date.getFullYear() === now.getFullYear()) {
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
  }

  // Anos anteriores: dia/mês/ano
  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

// ============================================================================
// 💬 FORMATEO DE PREVIEW DE MENSAJE (Lista de Conversas)
// ============================================================================

/**
 * Gera preview truncado para lista de conversas
 * 
 * @param {Object} message - Objeto da mensagem
 * @param {Object} options - Opções
 * @param {number} [options.maxLength=60] - Comprimento máximo
 * @param {boolean} [options.showPrefix=true] - Mostrar "Você: " para mensagens enviadas
 * @returns {string} Preview formatado
 */
export function formatMessagePreview(message, { maxLength = 60, showPrefix = true } = {}) {
  if (!message) return '';

  const body = message.content?.body || message.message || '';
  const type = message.content?.type || message.type || 'text';
  const direction = message.direction;

  // Anexos: mostrar tipo em vez de conteúdo
  if (type !== 'text' && type !== 'template') {
    const mediaLabels = {
      image: '📷 Imagem',
      video: '🎬 Vídeo',
      audio: '🎵 Áudio',
      document: '📎 Documento',
    };
    const prefix = direction === 'outbound' && showPrefix ? 'Você: ' : '';
    return prefix + (mediaLabels[type] || `📎 ${type}`);
  }

  // Texto: truncar
  let preview = body.replace(/\n/g, ' ').trim();
  const prefix = direction === 'outbound' && showPrefix ? 'Você: ' : '';
  const maxBodyLength = maxLength - prefix.length;

  if (preview.length > maxBodyLength) {
    // Cortar no espaço mais próximo
    const truncated = preview.slice(0, maxBodyLength);
    const lastSpace = truncated.lastIndexOf(' ');
    preview = lastSpace > maxBodyLength * 0.6
      ? truncated.slice(0, lastSpace) + '...'
      : truncated + '...';
  }

  return prefix + preview;
}

// ============================================================================
// 🔢 FORMATEO DE CONTADOR DE NÃO LIDOS
// ============================================================================

/**
 * Formata contador de mensagens não lidas
 * - 0: "" (vazio, não mostra)
 * - 1-99: número
 * - 99+: "99+"
 * 
 * @param {number} count - Quantidade de não lidas
 * @returns {string} Contador formatado
 */
export function formatUnreadCount(count) {
  if (!count || count <= 0) return '';
  if (count > 99) return '99+';
  return String(count);
}

// ============================================================================
// 📁 FORMATEO DE TAMANHO DE ARQUIVO
// ============================================================================

/**
 * Formata tamanho de arquivo para exibição
 * 
 * @param {number} bytes - Tamanho em bytes
 * @param {number} [decimals=1] - Casas decimais
 * @returns {string} Tamanho formatado (ex: "2.5 MB")
 */
export function formatFileSize(bytes, decimals = 1) {
  if (!bytes || bytes === 0) return '0 B';
  if (bytes < 0) return '0 B';

  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  // Limitar ao índice máximo do array
  const index = Math.min(i, sizes.length - 1);

  return `${parseFloat((bytes / Math.pow(k, index)).toFixed(decimals))} ${sizes[index]}`;
}

// ============================================================================
// 📞 FORMATEO DE TELEFONE INTERNACIONAL (WhatsApp)
// ============================================================================

/**
 * Formata número para link wa.me (WhatsApp)
 * Remove tudo exceto dígitos, garante prefixo 55
 * 
 * @param {string} phone - Número de telefone
 * @returns {string} Número limpo para URL (ex: "5511999999999")
 */
export function formatPhoneForWhatsApp(phone) {
  if (!phone) return '';
  const digits = String(phone).replace(/\D/g, '');
  if (digits.length < 10) return '';
  return digits.startsWith('55') ? digits : `55${digits}`;
}

/**
 * Gera link wa.me completo para WhatsApp
 * 
 * @param {string} phone - Número de telefone
 * @param {string} [message] - Mensagem opcional
 * @returns {string} URL do WhatsApp
 */
export function formatWhatsAppLink(phone, message = '') {
  const cleanPhone = formatPhoneForWhatsApp(phone);
  if (!cleanPhone) return '#';
  
  const base = `https://wa.me/${cleanPhone}`;
  return message ? `${base}?text=${encodeURIComponent(message)}` : base;
}

// ============================================================================
// 📊 FORMATEO DE STATUS DE MENSAGEM (Legível)
// ============================================================================

/**
 * Retorna texto legível para status de mensagem
 * 
 * @param {string} status - Status da mensagem ('sending', 'sent', 'delivered', 'read', 'failed')
 * @returns {string} Texto formatado
 */
export function formatMessageStatus(status) {
  const labels = {
    sending:   'Enviando...',
    sent:      'Enviada',
    delivered: 'Entregue',
    read:      'Lida',
    failed:    'Falha no envio',
  };
  return labels[status] || status || 'Desconhecido';
}

// ============================================================================
// 👤 FORMATEO DE NOME DE CONTATO
// ============================================================================

/**
 * Formata nome para exibição em lista de conversas
 * Se muito longo, trunca com "..."
 * 
 * @param {string} name - Nome completo
 * @param {number} [maxLength=25] - Comprimento máximo
 * @returns {string} Nome formatado
 */
export function formatContactName(name, maxLength = 25) {
  if (!name || typeof name !== 'string') return 'Desconhecido';
  
  const trimmed = name.trim();
  if (trimmed.length <= maxLength) return trimmed;

  // Tenta cortar no espaço
  const truncated = trimmed.slice(0, maxLength);
  const lastSpace = truncated.lastIndexOf(' ');
  
  return lastSpace > maxLength * 0.5
    ? truncated.slice(0, lastSpace) + '...'
    : truncated + '...';
}

// ============================================================================
// 🏷️ FORMATEO DE ETIQUETA DE DATA (Separador no Chat)
// ============================================================================

/**
 * Formata label para separador de data no chat
 * - Hoje: "Hoje"
 * - Ontem: "Ontem"
 * - Resto: "15 de Janeiro de 2026"
 * 
 * @param {Date|number} date - Data
 * @returns {string} Label formatada
 */
export function formatDateLabel(date) {
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return '';

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  if (d >= today) return 'Hoje';
  if (d >= yesterday) return 'Ontem';

  return d.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
}

// ============================================================================
// ✂️ FORMATEO DE TEXTO PARA CLIPBOARD
// ============================================================================

/**
 * Formata mensagem para copiar ao clipboard
 * Inclui remetente, data e conteúdo
 * 
 * @param {Object} message - Objeto da mensagem
 * @returns {string} Texto formatado para clipboard
 */
export function formatMessageForClipboard(message) {
  if (!message) return '';

  const sender = message.direction === 'outbound' ? '📤 Você' : '📥 Cliente';
  const time = formatConversationTime(message.timestamp || message.createdAt);
  const body = message.content?.body || message.message || '';

  return `${sender} · ${time}\n${body}`;
}

// ============================================================================
// 🔗 FORMATEO DE LINK PARA PROPRIEDADE (Contexto do Imóvel)
// ============================================================================

/**
 * Gera texto de contexto do imóvel para preview na conversa
 * 
 * @param {Object} property - Dados do imóvel { title, code, price }
 * @returns {string} Texto formatado ou vazio
 */
export function formatPropertyContext(property) {
  if (!property) return '';
  
  const parts = [];
  if (property.code) parts.push(`🏠 ${property.code}`);
  if (property.title) parts.push(property.title);
  
  return parts.length > 0 ? parts.join(' · ') : '';
}

// ============================================================================
// 🧊 CONGELAR PARA EVITAR MUTACIONES
// ============================================================================
// Todas las funciones son puras, no hay objetos mutables que congelar.
// Si en el futuro se añaden constantes de formateo, congelarlas aquí.